import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Button, Checkbox, Col, Flex, Form, Input, Row } from "antd";

const isNoneOption = (opt) => {
	if (!opt || typeof opt !== "string") return false;
	const str = opt.trim().toLowerCase();
	return (
		str === "none of the above" ||
		str === "none" ||
		str.startsWith("none of the above") ||
		str.startsWith("none of these")
	);
};

export default function RenderCheckbox(props) {
	const {
		namePath = [],
		form_question_options = [],
		required = false,
		question = null,
		description = null,
		add_another = false,
		max_checkbox = 0,
	} = props;
	let newNamePath = Array.isArray(namePath) ? namePath.join("_") : namePath;

	const [inputs, setInputs] = useState(add_another ? [{ id: 0 }] : []);

	const handleAddAnother = () => {
		setInputs((prev) => [...prev, { id: prev.length }]);
	};

	const renderCheckboxField = (fieldName) => (
		<Form.Item shouldUpdate noStyle>
			{({ getFieldValue }) => {
				const rawVal = getFieldValue(fieldName);
				const selectedValue = Array.isArray(rawVal) ? rawVal : [];
				const findOther = form_question_options.find((x) => x.is_other)?.option;
				const noneOption = form_question_options.find((x) => isNoneOption(x.option));
				const isNoneSelected = Boolean(
					noneOption && selectedValue.includes(noneOption.option),
				);
				const isMaxReached = Boolean(
					max_checkbox &&
						Number(max_checkbox) > 0 &&
						selectedValue.length >= Number(max_checkbox),
				);

				return (
					<Form.Item
						name={fieldName}
						getValueFromEvent={(newCheckedValues) => {
							let updated = Array.isArray(newCheckedValues)
								? [...newCheckedValues]
								: [];

							if (noneOption) {
								const noneVal = noneOption.option;
								const wasNoneSelected = selectedValue.includes(noneVal);
								const isNoneInNew = updated.includes(noneVal);

								if (!wasNoneSelected && isNoneInNew) {
									// User selected "None of the above" -> clear all other options
									updated = [noneVal];
								} else if (
									wasNoneSelected &&
									isNoneInNew &&
									updated.length > 1
								) {
									// User had "None of the above" selected and clicked another option -> remove "None of the above"
									updated = updated.filter((v) => v !== noneVal);
								}
							}

							// Enforce max_checkbox
							if (
								max_checkbox &&
								Number(max_checkbox) > 0 &&
								updated.length > Number(max_checkbox)
							) {
								updated = updated.slice(0, Number(max_checkbox));
							}

							return updated;
						}}
						rules={[
							{
								required: required ? true : false,
								message: "Please select at least one option",
							},
						]}
					>
						<Checkbox.Group>
							<Flex align="baseline" gap={5} vertical>
								{form_question_options.map((option, index) => {
									const isThisNone = Boolean(
										noneOption && option.option === noneOption.option,
									);
									const isDisabled =
										(isNoneSelected && !isThisNone) ||
										(!selectedValue.includes(option.option) && isMaxReached);

									return (
										<Checkbox
											key={`${option.id}-${index}`}
											value={option.option}
											disabled={isDisabled}
										>
											<Flex align="center" gap={5}>
												{option.option}
												{option.is_other &&
												selectedValue &&
												selectedValue.includes(findOther) ? (
													<Form.Item name={`${fieldName}_other`} noStyle>
														<Input placeholder="Please specify" />
													</Form.Item>
												) : null}
											</Flex>
										</Checkbox>
									);
								})}
							</Flex>
						</Checkbox.Group>
					</Form.Item>
				);
			}}
		</Form.Item>
	);

	// If add_another is enabled, render for each input set
	if (add_another) {
		return (
			<>
				{inputs.map((input, idx) => (
					<Col
						xs={24}
						sm={24}
						md={24}
						lg={24}
						xl={24}
						key={`input_set_${input.id}`}
					>
						{(question || description) && (
							<Row>
								<Col xs={24} sm={24} md={24} lg={24} xl={24}>
									{question && (
										<div
											className="quill-output question"
											dangerouslySetInnerHTML={{
												__html: question,
											}}
										/>
									)}
									{description && (
										<div className="description">{description}</div>
									)}
								</Col>
							</Row>
						)}
						<Row>
							<Col xs={24} sm={24} md={24} lg={24} xl={24}>
								{renderCheckboxField(`${newNamePath}_set_${idx}`)}
							</Col>
						</Row>
					</Col>
				))}

				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					<Button
						type="link"
						className="w-auto h-auto p-0"
						icon={<FontAwesomeIcon icon={faPlus} />}
						onClick={handleAddAnother}
					>
						Add Another
					</Button>
				</Col>
			</>
		);
	}

	return (
		<Row>
			{(question || description) && (
				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					{question && (
						<div
							className="quill-output question"
							dangerouslySetInnerHTML={{
								__html: question,
							}}
						/>
					)}
					{description && <div className="description">{description}</div>}
				</Col>
			)}
			<Col xs={24} sm={24} md={24} lg={24} xl={24}>
				{renderCheckboxField(newNamePath)}
			</Col>
		</Row>
	);
}
